/**
 * Embedded Components: the iframe side of the parent <-> iframe postMessage channel.
 *
 * Rules (see the partner guide):
 *  - Messages go ONLY to the parent origin the server returned at token exchange -- never "*", never an
 *    origin read from the URL. Until the exchange succeeds nothing is posted at all.
 *  - Incoming messages are accepted only from window.parent at that same origin.
 *  - Payloads carry ids, status and codes only: no names, emails, phone numbers, tokens or document data.
 *
 * Envelope: { source: "inkfree-embed", v: 1, type, requestId, ts, data }.
 */
import { IS_EMBED } from "./embedMode";

export type EmbedScope = "signing" | "creation";

export type EmbedEventType =
  | "ready"
  | "resize"
  | "signer_viewed"
  | "sign_started"
  | "sign_completed"
  | "sign_declined"
  | "approval_viewed"
  | "approval_completed"
  | "approval_rejected"
  | "send_completed"
  | "error";

export type EmbedErrorCode =
  | "TOKEN_INVALID"
  | "TOKEN_EXPIRED"
  | "TOKEN_USED"
  | "SESSION_EXPIRED"
  | "NOT_YOUR_TURN"
  | "REQUEST_NOT_FOUND"
  | "REQUEST_EXPIRED"
  | "REQUEST_VOIDED"
  | "REQUEST_DECLINED"
  | "REQUEST_COMPLETED"
  | "ORIGIN_MISMATCH"
  | "SIGNER_LOCKED"
  | "VALIDATION_FAILED"
  | "INSUFFICIENT_CREDITS"
  | "PLAN_LIMIT"
  | "VIEWPORT_TOO_SMALL"
  | "NETWORK"
  | "INTERNAL";

export interface EmbedTheme {
  primaryColor?: string;
  buttonTextColor?: string;
}

interface EmbedContext {
  scope: EmbedScope | null;
  requestId: string;
  parentOrigin: string | null;
  sessionSecret: string | null;
}

const SOURCE = "inkfree-embed";
const PARENT_SOURCE = "inkfree-embed-sdk";
const VERSION = 1;
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const context: EmbedContext = {
  scope: null,
  requestId: "",
  parentOrigin: null,
  sessionSecret: null,
};

const sentOnce = new Set<string>();
let themeOverride: EmbedTheme = {};
const themeListeners = new Set<(theme: EmbedTheme) => void>();
let listening = false;
let resizeObserver: ResizeObserver | null = null;

/**
 * The origin of the page that embeds this iframe, as the BROWSER reports it -- never read from the URL,
 * so the embedding page cannot choose it. `ancestorOrigins` (Chrome, Edge, Safari) lists every ancestor;
 * they must all be the same origin, so a registered page that is itself framed by someone else is refused.
 * Firefox has no `ancestorOrigins`, so the referrer (the SDK sends the origin only) stands in for the
 * direct parent. Returns "" when it cannot be established, and the server then refuses the token.
 */
export const detectFrameOrigin = (): string => {
  const ancestors: string[] = Array.from(
    (window.location as any).ancestorOrigins ?? [],
  );
  if (ancestors.length > 0) {
    const first = ancestors[0];
    return first && first !== "null" && ancestors.every((a) => a === first)
      ? first
      : "";
  }
  try {
    return document.referrer ? new URL(document.referrer).origin : "";
  } catch {
    return "";
  }
};

export const setEmbedContext =(next: Partial<EmbedContext>) => {
  Object.assign(context, next);
};

export const getEmbedContext = (): Readonly<EmbedContext> => context;

/** Headers for the embed editor endpoints: the session secret only -- no Authorization, no workspace. */
export const getEmbedSessionHeaders = (): Record<string, string> =>
  context.sessionSecret ? { "X-Embed-Session": context.sessionSecret } : {};

export const postEmbedEvent = (
  type: EmbedEventType,
  data: Record<string, unknown> = {},
) => {
  if (!IS_EMBED || !context.parentOrigin || window.parent === window) return;
  try {
    window.parent.postMessage(
      {
        source: SOURCE,
        v: VERSION,
        type,
        requestId: context.requestId,
        ts: Date.now(),
        data,
      },
      context.parentOrigin,
    );
  } catch {
    // A parent that navigated away is not an error for the signer.
  }
};

/** For events the contract fires once per page load (signer_viewed, sign_started, the terminal ones). */
export const postEmbedEventOnce = (
  type: EmbedEventType,
  data: Record<string, unknown> = {},
) => {
  if (sentOnce.has(type)) return;
  sentOnce.add(type);
  postEmbedEvent(type, data);
};

export const postEmbedError = (code: EmbedErrorCode) => {
  postEmbedEvent("error", { code, recoverable: isRecoverable(code) });
};

export const isRecoverable = (code: EmbedErrorCode): boolean =>
  code === "VALIDATION_FAILED" ||
  code === "VIEWPORT_TOO_SMALL" ||
  code === "NETWORK";

const validTheme = (raw: any): EmbedTheme => {
  const theme: EmbedTheme = {};
  if (raw && typeof raw === "object") {
    if (typeof raw.primaryColor === "string" && HEX_COLOR.test(raw.primaryColor)) {
      theme.primaryColor = raw.primaryColor;
    }
    if (
      typeof raw.buttonTextColor === "string" &&
      HEX_COLOR.test(raw.buttonTextColor)
    ) {
      theme.buttonTextColor = raw.buttonTextColor;
    }
  }
  return theme;
};

export const getThemeOverride = (): EmbedTheme => themeOverride;

/** Calls back with the parent's validated theme now (if already received) and whenever it arrives. */
export const subscribeTheme = (listener: (theme: EmbedTheme) => void) => {
  themeListeners.add(listener);
  if (themeOverride.primaryColor || themeOverride.buttonTextColor) {
    listener(themeOverride);
  }
  return () => {
    themeListeners.delete(listener);
  };
};

const onParentMessage = (event: MessageEvent) => {
  if (
    !context.parentOrigin ||
    event.source !== window.parent ||
    event.origin !== context.parentOrigin
  ) {
    return;
  }
  const message = event.data;
  if (!message || message.source !== PARENT_SOURCE || message.v !== VERSION) {
    return;
  }
  if (message.type === "theme") {
    themeOverride = validTheme(message.data);
    themeListeners.forEach((listener) => listener(themeOverride));
  }
};

/** Keeps the parent's iframe as tall as the content, so the partner page scrolls instead of the frame. */
const startAutoResize = () => {
  if (resizeObserver || typeof ResizeObserver === "undefined") return;
  let last = 0;
  const report = () => {
    const height = Math.ceil(document.documentElement.scrollHeight);
    if (height !== last) {
      last = height;
      postEmbedEvent("resize", { height });
    }
  };
  resizeObserver = new ResizeObserver(report);
  resizeObserver.observe(document.body);
  report();
};

/** Call once the exchange has returned the trusted parent origin: opens the channel and says hello. */
export const startEmbedChannel = (parentOrigin: string) => {
  setEmbedContext({ parentOrigin });
  if (!listening) {
    window.addEventListener("message", onParentMessage);
    listening = true;
  }
  postEmbedEvent("ready", {});
  startAutoResize();
};

/**
 * Turns an API failure into an embed error code. The backend prefixes embed errors with a stable code
 * ("TOKEN_USED: ..."); anything else is classified by HTTP status and the known signing messages.
 */
export const embedErrorCode = (error: any): EmbedErrorCode => {
  const message: string = String(error?.message ?? error?.errorMessage ?? "");
  const prefix = message.match(/^([A-Z_]+):/)?.[1];
  const known: EmbedErrorCode[] = [
    "TOKEN_INVALID",
    "TOKEN_EXPIRED",
    "TOKEN_USED",
    "SESSION_EXPIRED",
    "NOT_YOUR_TURN",
    "REQUEST_NOT_FOUND",
    "REQUEST_EXPIRED",
    "REQUEST_VOIDED",
    "REQUEST_DECLINED",
    "REQUEST_COMPLETED",
    "ORIGIN_MISMATCH",
    "VALIDATION_FAILED",
  ];
  if (prefix && (known as string[]).includes(prefix)) {
    return prefix as EmbedErrorCode;
  }
  const lower = message.toLowerCase();
  const status = error?.statusCode ?? error?.status;
  if (status === 401) return "SESSION_EXPIRED";
  if (status === 429) return "SIGNER_LOCKED";
  if (status === 402 || lower.includes("credit")) return "INSUFFICIENT_CREDITS";
  if (status === 403 || status === 423) return "PLAN_LIMIT";
  if (lower.includes("voided")) return "REQUEST_VOIDED";
  if (lower.includes("expired")) return "REQUEST_EXPIRED";
  if (lower.includes("declined")) return "REQUEST_DECLINED";
  // "turn" as a whole word only: a bare substring also matches "return", "returned", ...
  if (/\bturn\b/.test(lower) || lower.includes("order has been changed")) {
    return "NOT_YOUR_TURN";
  }
  if (status === undefined || error?.name === "TypeError") return "NETWORK";
  return "INTERNAL";
};

/** Fixed, generic text per code -- server messages are never shown inside the partner's page. */
export const embedErrorText: Record<EmbedErrorCode, string> = {
  TOKEN_INVALID: "This link is not valid.",
  TOKEN_EXPIRED: "This link has expired.",
  TOKEN_USED: "This link was already used.",
  SESSION_EXPIRED: "Your session has ended.",
  NOT_YOUR_TURN: "It is not your turn to sign yet.",
  REQUEST_NOT_FOUND: "This document is no longer available.",
  REQUEST_EXPIRED: "This document has expired.",
  REQUEST_VOIDED: "This document was cancelled by the sender.",
  REQUEST_DECLINED: "This document was declined.",
  REQUEST_COMPLETED: "This document is already complete.",
  ORIGIN_MISMATCH: "This page is not allowed to show this content.",
  SIGNER_LOCKED: "Signing is locked for this document.",
  VALIDATION_FAILED: "Please complete the highlighted items and try again.",
  INSUFFICIENT_CREDITS: "This document cannot be sent right now.",
  PLAN_LIMIT: "This feature is not available for this account.",
  VIEWPORT_TOO_SMALL: "This window is too small. Please enlarge it.",
  NETWORK: "We could not reach the server. Please try again.",
  INTERNAL: "Something went wrong.",
};
